import { Message, MessageDeliveryStatus } from '../../models/message';
import type { MessageDeliveryState, MessageDeliveryUpdate } from '../../services/socketService';

const DELIVERY_RANK: Record<MessageDeliveryStatus, number> = {
  PENDING: 1,
  SENT: 2,
  DELIVERED: 3,
  READ: 4,
  FAILED: 5,
};

const requestIdFromLogicalKey = (logicalSendKey?: string): string | undefined => {
  if (!logicalSendKey?.startsWith('admin:')) return undefined;
  const parts = logicalSendKey.split(':');
  return parts.length >= 3 ? parts.at(-1) : undefined;
};

export const messageClientRequestId = (message: Pick<Message, 'clientRequestId' | 'logicalSendKey'>): string | undefined =>
  message.clientRequestId ?? requestIdFromLogicalKey(message.logicalSendKey);

// A `local:<clientRequestId>` id is the client-minted optimistic id, never a server one.
// The typeof guard covers a row built from loosely-validated socket/HTTP payloads
// where `id` did not survive as a string despite the Message type promising one.
export const serverMessageId = (message: Pick<Message, 'id'>): string | undefined =>
  typeof message.id === 'string' && !message.id.startsWith('local:') ? message.id : undefined;

const hasSameImmutableEnvelope = (left: Message, right: Message): boolean =>
  String(left.sessionId) === String(right.sessionId) &&
  left.workerId === right.workerId &&
  left.message === right.message &&
  left.direction === right.direction &&
  left.senderType === right.senderType;

const hasCoherentRequestIdentity = (message: Message): boolean => {
  const logicalRequestId = requestIdFromLogicalKey(message.logicalSendKey);
  return !message.clientRequestId || !logicalRequestId ||
    message.clientRequestId === logicalRequestId;
};

const requestIdentitiesAreCompatible = (left: Message, right: Message): boolean => {
  if (!hasCoherentRequestIdentity(left) || !hasCoherentRequestIdentity(right)) return false;
  if (
    left.clientRequestId &&
    right.clientRequestId &&
    left.clientRequestId !== right.clientRequestId
  ) return false;
  if (
    left.logicalSendKey &&
    right.logicalSendKey &&
    left.logicalSendKey !== right.logicalSendKey
  ) return false;
  const leftRequestId = messageClientRequestId(left);
  const rightRequestId = messageClientRequestId(right);
  return !leftRequestId || !rightRequestId || leftRequestId === rightRequestId;
};

const sameLogicalMessage = (left: Message, right: Message): boolean => {
  // The server id is authoritative: a voiced release rewrites the body of the
  // same row, so the body-based envelope check must not run first. A falsy id
  // on both sides is "no id yet", not a shared identity. The client-minted
  // `local:` id is not a server id either, so two optimistic rows never merge by id.
  const leftServerId = serverMessageId(left);
  const rightServerId = serverMessageId(right);
  if (leftServerId && rightServerId && leftServerId === rightServerId) return true;
  if (
    !hasSameImmutableEnvelope(left, right) ||
    !requestIdentitiesAreCompatible(left, right)
  ) return false;
  if (left.logicalSendKey && right.logicalSendKey && left.logicalSendKey === right.logicalSendKey) return true;
  const leftRequestId = messageClientRequestId(left);
  const rightRequestId = messageClientRequestId(right);
  return Boolean(leftRequestId && rightRequestId && leftRequestId === rightRequestId);
};

const latestStatus = (
  current?: MessageDeliveryStatus,
  incoming?: MessageDeliveryStatus,
): MessageDeliveryStatus | undefined => {
  if (!current) return incoming;
  if (!incoming) return current;
  return DELIVERY_RANK[incoming] >= DELIVERY_RANK[current] ? incoming : current;
};

const byReceivedAt = (left: Message, right: Message): number => {
  const receivedAtDelta = new Date(left.receivedAt).getTime() - new Date(right.receivedAt).getTime();
  if (receivedAtDelta !== 0) return receivedAtDelta;
  // Ascending id is the reverse of the backend page's `received_at DESC, id DESC`, so a
  // same-timestamp batch renders in the order the server returns it, read oldest-first.
  // An id-less row deliberately sorts first within a tie.
  const leftId = serverMessageId(left) ?? '';
  const rightId = serverMessageId(right) ?? '';
  return leftId < rightId ? -1 : leftId > rightId ? 1 : 0;
};

export const mergeConversationMessage = (messages: Message[], incoming: Message): Message[] => {
  const matching = messages.filter((message) => sameLogicalMessage(message, incoming));
  if (matching.length === 0) return [...messages, incoming].sort(byReceivedAt);

  const merged = matching.reduce<Message>((result, current) => ({
    ...current,
    ...result,
    clientRequestId: messageClientRequestId(result) ?? messageClientRequestId(current),
    deliveryStatus: latestStatus(current.deliveryStatus, result.deliveryStatus),
  }), {
    ...incoming,
    clientRequestId: messageClientRequestId(incoming),
  });

  return [...messages.filter((message) => !sameLogicalMessage(message, merged)), merged].sort(byReceivedAt);
};

export const reconcileConversationMessages = (current: Message[], authoritative: Message[]): Message[] =>
  authoritative.reduce((messages, serverMessage) => {
    const reconciled = mergeConversationMessage(messages, serverMessage);
    if (!serverMessage.deliveryStatus) return reconciled;
    return reconciled.map((message) => sameLogicalMessage(message, serverMessage)
      ? { ...message, deliveryStatus: serverMessage.deliveryStatus }
      : message);
  }, current);

// Provider states that mean "no terminal outcome yet" — a delivery still in flight
// or of unknown outcome. The terminal fence below (isTerminalDeliveryState) is
// derived from what is left of the union once these are excluded.
type NonTerminalDeliveryState = Extract<MessageDeliveryState, 'HELD' | 'PENDING' | 'CLAIMED' | 'UNKNOWN'>;

// Keyed on every terminal MessageDeliveryState member so an added union member is a
// compile error here, not a silently-fell-through default.
const TERMINAL_STATUS: Record<Exclude<MessageDeliveryState, NonTerminalDeliveryState>, MessageDeliveryStatus> = {
  ACCEPTED: 'SENT',
  DELIVERED: 'DELIVERED',
  READ: 'READ',
  FAILED: 'FAILED',
  DEAD_LETTERED: 'FAILED',
};

// The single decision point for terminal-vs-not: an unrecognized state (including
// one that names an Object.prototype member such as `constructor` or `toString`)
// fails closed as non-terminal instead of resolving to a prototype value.
export const isTerminalDeliveryState = (
  state: string,
): state is Exclude<MessageDeliveryState, NonTerminalDeliveryState> =>
  Object.prototype.hasOwnProperty.call(TERMINAL_STATUS, state);

const deliveryStatusFromProviderState = (state: MessageDeliveryState): MessageDeliveryStatus =>
  isTerminalDeliveryState(state) ? TERMINAL_STATUS[state] : 'PENDING';

export const applyMessageDeliveryUpdate = (
  messages: Message[],
  update: MessageDeliveryUpdate,
): Message[] => {
  const status = deliveryStatusFromProviderState(update.status);
  return messages.map((message) => {
    const requestId = messageClientRequestId(message);
    const matches = (update.messageId && serverMessageId(message) === update.messageId)
      || (update.logicalSendKey && message.logicalSendKey === update.logicalSendKey)
      || (update.clientRequestId && requestId === update.clientRequestId);
    if (!matches) return message;
    const deliveryStatus = status === 'PENDING'
      ? latestStatus(message.deliveryStatus, status)
      : status;
    return { ...message, deliveryStatus };
  });
};
